import React, {useEffect, useState} from 'react';
import {useNavigate} from "react-router-dom";
import ButtonHint from "../../../../components/Buttons/buttonHint/ButtonHint";
import CustomAlert from "../../../../components/CustomAlert/CustomAlert";
import {MEDIA} from "../../../../utils/js/Const";
import {usePatchOrder} from "../../hooks/usePatchOrder/usePatchOrder";

const OrderDetailsEdit = ({detailInformation}) => {
    const navigate = useNavigate();
    const {patchOrder, loading: patching, error: patchError} = usePatchOrder();

    const [quantities, setQuantities] = useState({});
    const [checkedItems, setCheckedItems] = useState({});

    const [alertState, setAlertState] = useState({
        isOpen: false, title: '', message: '', statusCod: '', isSuccess: false,
    });

    const showAlert = (title, message, statusCod, isSuccess = false) => {
        setAlertState({isOpen: true, title, message, statusCod, isSuccess});
    };

    const closeAlert = () => {
        const wasSuccess = alertState.isSuccess;
        setAlertState(prev => ({...prev, isOpen: false}));

        if (wasSuccess) {
            navigate('/Orders');
        }
    };

    // Ошибка PATCH
    useEffect(() => {
        if (patchError) {
            showAlert(patchError.title || 'Ошибка', patchError.message, patchError.statusCode);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [patchError]);

    // Инициализация количеств
    useEffect(() => {
        if (detailInformation?.goods) {
            const initialQty = {};
            detailInformation.goods.forEach(item => {
                initialQty[item.guid] = item.quantity;
            });
            setQuantities(initialQty);
        }
    }, [detailInformation]);

    const getOriginal = (guid) => {
        const item = detailInformation?.goods?.find(g => g.guid === guid);
        return item?.quantity ?? 0;
    };

    const isChecked = (guid) => !!checkedItems[guid];
    const toggleChecked = (guid) => {
        setCheckedItems(prev => ({...prev, [guid]: !prev[guid]}));
    };

    const canEdit = (guid) => !isChecked(guid);

    const handlePlus = (guid) => {
        if (!canEdit(guid)) return;
        const original = getOriginal(guid);
        const current = quantities[guid] ?? original;
        if (current >= original) return;
        setQuantities(prev => ({...prev, [guid]: current + 1}));
    };

    const handleMinus = (guid) => {
        if (!canEdit(guid)) return;
        const current = quantities[guid] ?? 0;
        if (current <= 0) return;
        setQuantities(prev => ({...prev, [guid]: current - 1}));
    };

    const handleInputChange = (guid, value) => {
        if (!canEdit(guid)) return;
        const original = getOriginal(guid);
        const parsed = Number(value);
        if (Number.isNaN(parsed)) return;
        const clamped = Math.max(0, Math.min(parsed, original));
        setQuantities(prev => ({...prev, [guid]: clamped}));
    };

    const canPlus = (guid) => {
        if (!canEdit(guid)) return false;
        const original = getOriginal(guid);
        const current = quantities[guid] ?? original;
        return current < original;
    };

    const canMinus = (guid) => {
        if (!canEdit(guid)) return false;
        const current = quantities[guid] ?? 0;
        return current > 0;
    };

    const allChecked = (detailInformation?.goods?.length ?? 0) > 0
        && detailInformation.goods.every(item => isChecked(item.guid));

    const handleSubmit = async () => {
        const goods = (detailInformation?.goods || []).map(item => ({
            guid: item.guid,
            quantity: quantities[item.guid] ?? item.quantity,
        }));

        try {
            await patchOrder(detailInformation.guid, goods);
            showAlert(
                'Успех',
                'Вы успешно собрали заказ',
                200,
                true
            );
        } catch (err) {
            console.error('Ошибка сохранения изменений:', err);
        }
    };

    const formatDateTime = (isoString) => {
        if (!isoString) return '';
        const date = new Date(isoString);
        if (isNaN(date.getTime())) return '';

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');

        return `${hours}:${minutes} ${day}.${month}.${year}`;
    };

    return (
        <div className="order_details-list">
            <CustomAlert
                title={alertState.title}
                message={alertState.message}
                statusCod={alertState.statusCod}
                isOpenAlert={alertState.isOpen}
                onClose={closeAlert}
            />

            <div className={"title"}>Заказ (сборка)</div>

            <div className={"order_details-list--header"}>
                <div>Заказ №: {detailInformation?.guid?.split('-')[2]}</div>
                <div>от {formatDateTime(detailInformation.datetime_create)}</div>
            </div>

            <div className={"order_details-list--body"}>
                <div className={"name"}>Товары:</div>
                {detailInformation?.goods?.map((order) => {
                    const checked = isChecked(order.guid);
                    const plusEnabled = canPlus(order.guid);
                    const minusEnabled = canMinus(order.guid);

                    return (
                        <div
                            className={`item ${checked ? "item--checked" : ""}`}
                            key={order.guid}
                        >
                            <div className={"item-img_name"}>
                                <input
                                    type="checkbox"
                                    className={"item-checkbox"}
                                    checked={checked}
                                    onChange={() => toggleChecked(order.guid)}
                                />
                                <img
                                    className={"item-img_name-img"}
                                    src={`${MEDIA}/api/attachments/${order?.attachments_guid_array}`}
                                    alt={order.name}
                                />
                                <div className={"item-img_name-name"}>{order.name}</div>
                            </div>

                            <div className={"item-price"}>
                                <div className={"current-price"}>{order.current_price}₽</div>
                                {order.old_price > 0 && (
                                    <div className={"old-price"}>{order.old_price}₽</div>
                                )}
                            </div>

                            <div className={"item-quantity"}>
                                <div
                                    className={`item-quantity-button ${!plusEnabled ? "item-quantity-button--disabled" : ""}`}
                                    style={plusEnabled ? {backgroundColor: "var(--color--green)"} : undefined}
                                    onClick={() => plusEnabled && handlePlus(order.guid)}
                                >
                                    +
                                </div>

                                <input
                                    type="number"
                                    className={"item-quantity-input"}
                                    value={quantities[order.guid] ?? order.quantity}
                                    onChange={(e) => handleInputChange(order.guid, e.target.value)}
                                    min={0}
                                    max={order.quantity}
                                    disabled={checked}
                                />

                                <span className={"item-quantity-measure"}>
                                    {order.measure_name}
                                </span>

                                <div
                                    className={`item-quantity-button ${!minusEnabled ? "item-quantity-button--disabled" : ""}`}
                                    style={minusEnabled ? {backgroundColor: "var(--color--red)"} : undefined}
                                    onClick={() => minusEnabled && handleMinus(order.guid)}
                                >
                                    -
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            <ButtonHint
                text={patching ? "Сохраняем..." : "Заказ собран"}
                className={`order_details-page-button order_details-page-button-edit ${!allChecked || patching ? "order_details-page-button--disabled" : ""}`}
                funck={allChecked && !patching ? handleSubmit : undefined}
            />
        </div>
    );
};

export default OrderDetailsEdit;